import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, Check, CheckCircle2, ChevronDown } from 'lucide-react';
import { integrationLogos } from '../mock';
import { SEO } from './SEO';
import IntelligenceLayerDiagram from './landing/IntelligenceLayerDiagram';
import Footer from './Footer';
import { useActiveSignupFlowState } from '../hooks/useActiveSignupFlow';

// Daily outcomes — four distinct, descriptive benefits. Product-UI wells swap in at the image pass.
const dailyOutcomes = [
  {
    title: 'Your day plans itself',
    body: 'While you’re offline, your team assembles tomorrow before it starts — a prioritized schedule, briefs for every meeting, and the action items that actually matter. You wake up to a plan you can act on, not a blank calendar and a scramble to figure out where to begin.',
    placeholder: 'My Day · morning brief — schedule, meeting briefs, action items',
    // Floating-cards composition (was a single pre-composed webp). Tilt used sparingly:
    // the My Day agenda (hero) and the wide meeting card stay straight; only the two
    // supporting cards get a subtle lean.
    dir: '/images/home',
    aspect: 'aspect-[1/1]',
    layers: [
      { src: 'outcome-my-day-1.webp', cls: 'left-[1%] top-[3%] w-[34%]', transform: 'rotate(0deg)', z: 40, alt: 'My Day — a prioritized schedule of meetings, tasks and approvals' },
      { src: 'outcome-my-day-2.webp', cls: 'right-[2%] top-[2%] w-[56%]', transform: 'rotate(2deg)', z: 20, alt: 'Pending approvals waiting on you' },
      { src: 'outcome-my-day-4.webp', cls: 'right-[2%] top-[42%] w-[48%]', transform: 'rotate(0deg)', z: 30, alt: 'An approval, opened up — budget, projections and the why' },
      { src: 'outcome-my-day-3.webp', cls: 'left-[28%] bottom-[3%] w-[48%]', transform: 'rotate(-2deg)', z: 35, alt: 'A meeting brief — agenda and next steps' },
    ],
    imageAlt: 'The My Day view — a prioritized schedule, must-do list and pending approvals',
  },
  {
    title: 'Nothing slips through',
    body: 'Every thread, deadline, and commitment is watched across your tools, so the thing you meant to circle back to doesn’t quietly go cold. Agents surface what needs you and follow up at the right moment — the deal, the reply, the renewal — before it turns into a problem.',
    placeholder: 'Tracking · threads, deadlines & due follow-ups',
    // One detailed tracked item on top, a feed of watched items cascading below. Tilt
    // only on the feed rows (small), the hero card stays straight.
    dir: '/images/home',
    aspect: 'aspect-[1/1]',
    layers: [
      { src: 'outcome-tracking-1.webp', cls: 'left-1/2 top-[3%] w-[80%]', transform: 'translateX(-50%) rotate(0deg)', z: 30, alt: 'A completed follow-up — meeting notes and next steps' },
      { src: 'outcome-tracking-3.webp', cls: 'left-[5%] top-[58%] w-[60%]', transform: 'rotate(-1.5deg)', z: 20, alt: 'Revenue Forecast Review — due today' },
      { src: 'outcome-tracking-4.webp', cls: 'right-[5%] top-[71%] w-[60%]', transform: 'rotate(1.5deg)', z: 25, alt: 'Resolve Support Capacity Risk — critical' },
      { src: 'outcome-tracking-2.webp', cls: 'left-1/2 top-[86%] w-[56%]', transform: 'translateX(-50%) rotate(0deg)', z: 20, alt: 'Q1 Planning Sync — completed' },
    ],
    imageAlt: 'A tracking view — threads, deadlines and follow-ups due, watched across your tools',
  },
  {
    title: 'Decisions come pre-analyzed',
    body: 'When a call needs making, it doesn’t land on your desk raw. The context is gathered, the data is pulled, and a recommended next step is already attached — so you’re weighing a clear choice instead of starting the research yourself.',
    placeholder: 'Decision card · context, data & recommended next step',
    // Two analyzed decision cards on top; the two recommendation toasts run near
    // full-width beneath them so they stay readable (they're very wide/thin) and clear
    // of the cards. Tilt only on the second card + the toasts, kept small.
    dir: '/images/home',
    aspect: 'aspect-[1/1]',
    layers: [
      { src: 'outcome-decisions-1.webp', cls: 'left-[1%] top-[2%] w-[48%]', transform: 'rotate(0deg)', z: 30, alt: 'A pre-analyzed decision — budget, projections and the recommended call' },
      { src: 'outcome-decisions-2.webp', cls: 'right-[1%] top-[4%] w-[48%]', transform: 'rotate(1.5deg)', z: 25, alt: 'Lead quality dropped 18% — root cause and proposed resolution' },
      { src: 'outcome-decisions-3.webp', cls: 'left-1/2 top-[60%] w-[94%]', transform: 'translateX(-50%) rotate(-1deg)', z: 35, alt: 'A drafted re-engagement, recommended' },
      { src: 'outcome-decisions-4.webp', cls: 'left-1/2 top-[81%] w-[92%]', transform: 'translateX(-50%) rotate(0.8deg)', z: 35, alt: 'A counter-offer, ready for approval' },
    ],
    imageAlt: 'A decision card — the context gathered, the data pulled, and a recommended next step',
  },
  {
    title: 'Your inbox comes pre-drafted',
    body: 'Your inbox arrives triaged, with replies written in your voice and ready for a quick approve-and-send. The threads that matter get surfaced and the routine ones handled — so you clear it in minutes, and nothing important waits on you.',
    placeholder: 'Inbox · drafted replies ready to approve & send',
    // The snapshot and opened thread up top, then the agent's thinking band → the
    // finished draft reply. Tilt only on the thread + thinking band.
    dir: '/images/home',
    aspect: 'aspect-[1/1]',
    layers: [
      { src: 'outcome-inbox-1.webp', cls: 'left-[2%] top-[2%] w-[50%]', transform: 'rotate(0deg)', z: 30, alt: 'Inbox snapshot — the threads that matter, surfaced' },
      { src: 'outcome-inbox-2.webp', cls: 'right-[2%] top-[4%] w-[52%]', transform: 'rotate(2deg)', z: 20, alt: 'An opened thread with the context gathered' },
      { src: 'outcome-inbox-3.webp', cls: 'left-1/2 top-[40%] w-[92%]', transform: 'translateX(-50%) rotate(-1deg)', z: 35, alt: 'Drafting the reply — thinking, fetching, composing' },
      { src: 'outcome-inbox-4.webp', cls: 'left-1/2 top-[58%] w-[80%]', transform: 'translateX(-50%) rotate(0deg)', z: 40, alt: 'The drafted reply — ready for a one-click send' },
    ],
    imageAlt: 'The inbox — replies drafted in your voice, ready to approve and send',
  },
];

// Home use-cases showcase content (curated homepage copy; grounded in useCasesData.js).
// Each department lists the same three workflows, ordered by relevance to that team.
const homeDepartments = [
  {
    slug: 'sales',
    label: 'Sales',
    whatChanges: 'Your reps spend their hours selling — the CRM, follow-ups and prep move to the agents.',
    outcomes: [
      'Leads qualified and prioritized automatically',
      'Follow-ups drafted and sent on time, every time',
      'A CRM that stays true, so the forecast is real',
    ],
    workflows: [
      { label: 'CRM management', slug: 'crm-management' },
      { label: 'Pipeline Generation', slug: 'pipeline-generation' },
      { label: 'Focus & flow', slug: 'focus-and-flow' },
    ],
    caption: 'Sales command center — deals, pipeline, outreach and reporting, with Sally working in the background.',
    // Descending staircase: Reports and Deals views peek behind the Sales command
    // center (full, front) — same arrangement as the Services hero.
    dir: '/images/home',
    stack: [
      { src: 'dept-sales-3.webp', cls: 'left-[2%] top-[1%] w-[88%]', transform: 'rotate(-2deg)', z: 10, alt: 'Sales reports — revenue trend, win/loss and rep performance' },
      { src: 'dept-sales-2.webp', cls: 'left-[7%] top-[21%] w-[88%]', transform: 'rotate(-2deg)', z: 20, alt: 'The deals board — every deal by stage' },
      { src: 'dept-sales-1.webp', cls: 'left-[12%] top-[42%] w-[88%]', transform: 'rotate(-2deg)', z: 40, alt: 'The sales command center — alerts, active deals, pipeline and metrics' },
    ],
  },
  {
    slug: 'marketing',
    label: 'Marketing',
    whatChanges: 'Launches ship on time — the calendar, channels and reporting keep moving without the coordination chaos.',
    outcomes: [
      'A content calendar that runs itself',
      'Social scheduled across every channel, replies surfaced',
      'Campaign performance always current — no Monday spreadsheet',
    ],
    workflows: [
      { label: 'Pipeline Generation', slug: 'pipeline-generation' },
      { label: 'Focus & flow', slug: 'focus-and-flow' },
      { label: 'CRM management', slug: 'crm-management' },
    ],
    caption: 'Marketing command center — campaigns, content, social and analytics, with Marsi, Wright and Buzz at work.',
    // Descending staircase: Social and Campaigns views peek behind the Marketing
    // command center (full, front).
    dir: '/images/home',
    stack: [
      { src: 'dept-marketing-3.webp', cls: 'left-[2%] top-[1%] w-[88%]', transform: 'rotate(-2deg)', z: 10, alt: 'Social — per-channel stats, weekly calendar and Buzz activity' },
      { src: 'dept-marketing-2.webp', cls: 'left-[7%] top-[21%] w-[88%]', transform: 'rotate(-2deg)', z: 20, alt: 'The campaigns board — every campaign by status' },
      { src: 'dept-marketing--1.webp', cls: 'left-[12%] top-[42%] w-[88%]', transform: 'rotate(-2deg)', z: 40, alt: 'The marketing command center — campaigns, lead metrics and channel performance' },
    ],
  },
  {
    slug: 'personal-productivity',
    label: 'Personal Productivity',
    whatChanges: 'The leverage of a full team without hiring one — your day planned, your inbox triaged, your projects on track.',
    outcomes: [
      'Your day, planned overnight',
      'An inbox that already has your replies drafted',
      'Only the calls that actually need you',
    ],
    workflows: [
      { label: 'Focus & flow', slug: 'focus-and-flow' },
      { label: 'CRM management', slug: 'crm-management' },
      { label: 'Pipeline Generation', slug: 'pipeline-generation' },
    ],
    caption: 'Your Home, run by Luc — the morning brief: inbox, meetings, must-dos and approvals in one place.',
    // Descending staircase: the company-health overview peeks behind your My Day
    // morning brief (full, front).
    dir: '/images/home',
    stack: [
      { src: 'dept-personal-1.webp', cls: 'left-[2%] top-[1%] w-[88%]', transform: 'rotate(-2deg)', z: 10, alt: 'Company health — department scores, cross-team risks and agent activity' },
      { src: 'dept-personal-2.webp', cls: 'left-[12%] top-[40%] w-[88%]', transform: 'rotate(-2deg)', z: 40, alt: 'Your My Day — inbox, meetings, must-dos and pending approvals' },
    ],
  },
];

// Preview panel for the active department (screenshot placeholder + outcomes + workflows + CTA).
const DepartmentPreview = ({ d }) => (
  <div className="space-y-5">
    {/* Product screenshot — a descending staircase of the department's views (same
        arrangement as the Services hero) where we have a stack; else a single bare
        composition; else the placeholder well. */}
    {d.stack ? (
      <div className="relative aspect-[5/4]">
        {d.stack.map((L) => (
          <div
            key={L.src}
            className={`absolute ${L.cls}`}
            style={{ zIndex: L.z, transform: L.transform, filter: 'drop-shadow(0 14px 32px rgba(31, 38, 135, 0.16))' }}
          >
            <img src={`${d.dir}/${L.src}`} alt={L.alt || d.caption} className="block w-full h-auto rounded-lg" loading="lazy" />
          </div>
        ))}
      </div>
    ) : d.image ? (
      <img src={d.image} alt={d.caption} className="w-full h-auto" loading="lazy" />
    ) : (
      <div
        className="aspect-[16/10] rounded-2xl overflow-hidden flex items-center justify-center"
        style={{ border: '1px dashed rgba(20,20,43,0.18)', background: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.35) 0 16px, rgba(255,255,255,0.12) 16px 32px)' }}>
        <span className="text-xs md:text-sm font-mono text-center px-4" style={{ color: 'rgba(20,20,43,0.45)' }}>placeholder · {d.caption}</span>
      </div>
    )}
    {/* Outcomes */}
    <ul className="space-y-2.5">
      {d.outcomes.map((o, i) => (
        <li key={i} className="flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#00BFA6' }} />
          <span className="text-sm md:text-base" style={{ color: 'rgba(20, 20, 43, 0.75)' }}>{o}</span>
        </li>
      ))}
    </ul>
    {/* Workflows it runs */}
    <div>
      <p className="text-xs font-medium mb-2" style={{ color: 'rgba(20,20,43,0.5)' }}>Workflows it runs</p>
      <div className="flex flex-wrap gap-2">
        {d.workflows.map((w) => (
          <Link key={w.slug} to={`/use-cases/${w.slug}`} className="text-xs md:text-sm px-3 py-1.5 rounded-full transition-all duration-200 hover:scale-105" style={{ color: '#14142b', background: 'rgba(255, 255, 255, 0.5)', border: '1px solid rgba(20, 20, 43, 0.14)' }}>
            {w.label}
          </Link>
        ))}
      </div>
    </div>
    {/* Explore CTA (contextual) */}
    <Link to={`/use-cases/${d.slug}`} className="group inline-flex items-center gap-2 text-sm md:text-base font-medium transition-transform duration-200 hover:translate-x-1" style={{ color: '#D42A4B' }}>
      Explore {d.label}
      <ArrowRight className="h-4 w-4 md:h-5 md:w-5" />
    </Link>
  </div>
);

// Dept-led auto-advancing accordion + synced preview. Cards are select-only (no redirect).
const DepartmentShowcase = ({ departments }) => {
  const [active, setActive] = useState(0);

  // Auto-advance ~9s; `active` in deps so a manual click restarts the countdown.
  useEffect(() => {
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % departments.length);
    }, 9000);
    return () => clearInterval(timer);
  }, [active, departments.length]);

  const AccordionCard = ({ d, i, mobile = false }) => {
    const isActive = active === i;
    return (
      <div
        onClick={() => setActive(i)}
        className="cursor-pointer rounded-2xl overflow-hidden"
        style={{
          background: isActive ? 'rgba(10, 10, 35, 0.85)' : 'rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          border: isActive ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(255, 255, 255, 0.65)',
          boxShadow: '0 20px 50px rgba(31, 38, 135, 0.10)',
        }}>
        <div className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 md:gap-4">
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-base font-medium flex-shrink-0" style={{ backgroundColor: isActive ? '#FF5C5C' : '#FFD6D6', color: isActive ? 'white' : '#0A0A23' }}>{i + 1}</div>
              <h4 className={`${mobile ? 'text-base' : 'text-base md:text-lg'} font-medium`} style={{ color: isActive ? 'white' : '#14142b' }}>{d.label}</h4>
            </div>
            <ChevronDown className="h-5 w-5 flex-shrink-0 transition-transform duration-200" style={{ color: isActive ? 'rgba(255,255,255,0.6)' : 'rgba(20,20,43,0.6)', transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </div>
          {isActive && (
            <p className="mt-3 text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>{d.whatChanges}</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Desktop: accordion (40%) + synced preview (60%) */}
      <div className="hidden lg:grid grid-cols-5 gap-10 lg:gap-16 items-start">
        <div className="col-span-2 space-y-3">
          {departments.map((d, i) => (
            <AccordionCard key={d.slug} d={d} i={i} />
          ))}
        </div>
        <div className="col-span-3">
          <DepartmentPreview d={departments[active]} />
        </div>
      </div>

      {/* Mobile/Tablet: accordion with inline preview under the active item */}
      <div className="lg:hidden space-y-3">
        {departments.map((d, i) => (
          <div key={d.slug}>
            <AccordionCard d={d} i={i} mobile />
            {active === i && (
              <div className="mt-3">
                <DepartmentPreview d={d} />
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

// Intro video — self-hosted and fully clean (no third-party player chrome). The poster
// shows until the viewer clicks; then the video loads and plays with sound. No controls —
// clicking the video toggles play/pause. Framed full-width to match the product-demo
// video below. preload="none" so the mp4 isn't fetched until play.
const IntroVideo = () => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play(); else v.pause();
  };
  return (
    <section id="intro" className="py-8 md:py-12 relative" data-animate>
      <div className="max-w-[1500px] mx-auto px-4 md:px-6 lg:px-12">
        {/* Soft-embedded frame — the neumorphic treatment lives on the video edges
            themselves (a subtle dual light/dark shadow + a recessed inner edge), so it
            reads as set into the page with no extra bezel/frame around it. */}
        <div
          className="relative aspect-video rounded-2xl md:rounded-3xl overflow-hidden"
          style={{ boxShadow: '-8px -8px 22px rgba(255, 255, 255, 0.45), 10px 14px 34px rgba(31, 38, 135, 0.15)' }}>
          <video
            ref={videoRef}
            className="w-full h-full object-cover cursor-pointer"
            src="/images/home/implify-explainer.mp4"
            poster="/images/home/intro-poster.webp"
            playsInline
            preload="none"
            onClick={toggle}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            aria-label="Implify intro video — click to play or pause"
          />
          {/* recessed inner edge — makes the video read as set into the page */}
          <span
            className="pointer-events-none absolute inset-0 rounded-2xl md:rounded-3xl"
            style={{ boxShadow: 'inset 0 2px 12px rgba(31, 38, 135, 0.26), inset 0 0 0 1px rgba(31, 38, 135, 0.06)' }}
          />
          {!playing && (
            <button
              type="button"
              onClick={toggle}
              className="group absolute inset-0 w-full h-full flex items-center justify-center cursor-pointer"
              aria-label="Play the Implify intro video">
              <span className="absolute inset-0 transition-colors" style={{ background: 'rgba(20, 20, 43, 0.12)' }} />
              <span
                className="relative flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full transition-transform duration-200 group-hover:scale-105"
                style={{ background: '#FF5C5C', boxShadow: '0 12px 30px rgba(255, 92, 92, 0.45)' }}>
                <svg viewBox="0 0 24 24" fill="white" className="w-7 h-7 md:w-9 md:h-9 ml-1" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

const HomePage = () => {
  // Paywall flag: only the pricing-teaser micro-copy is flow-dependent (legacy "no card" ↔
  // paywall "$0 due today"). `resolved` gates it so a cold load never flips wrong→right.
  const { flow: activeSignupFlow, resolved: signupFlowResolved } = useActiveSignupFlowState();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState({});
  const [scrollY, setScrollY] = useState(0);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // How-it-works video: play only while it's on screen (perf + battery), and hold on
  // the poster when the user prefers reduced motion. Paired with preload="none" so the
  // file isn't fetched until it actually plays.
  const howItWorksVideoRef = useRef(null);
  useEffect(() => {
    const v = howItWorksVideoRef.current;
    if (!v) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return; // stay on the poster
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) v.play().catch(() => {});
          else v.pause();
        });
      },
      { threshold: 0.25 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  return (
    <div className="overflow-x-hidden" style={{ minHeight: '100vh' }}>
      {/* Fixed, viewport-locked aurora backdrop (iOS-safe; replaces background-attachment:fixed, CP7.2) */}
      <div className="aurora-gradient" aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: -1 }} />
      {/* SEO Meta Tags */}
      <SEO page="home" />
      
      {/* Hero Section */}
      <section
        id="hero"
        className="min-h-screen flex items-center px-4 md:px-6 lg:px-12 pt-20 md:pt-24 pb-16 md:pb-24 relative overflow-hidden"
        data-animate>

        {/* Master Card Container - Glassmorphism */}
        <div
          className="max-w-7xl mx-auto w-full relative"
          style={{
            borderRadius: '24px',
            padding: 'clamp(24px, 5vw, 64px)',
            background: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.65)',
            boxShadow: '0 20px 50px rgba(31, 38, 135, 0.10)'
          }}>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Column - Text Content */}
            <div className="relative z-10 text-center lg:text-left">
              
              {/* Mobile/Tablet agent cards — staggered fanned stack (desktop cascade on lg+).
                  Tilt lives on the wrapper so it coexists with float-card (which owns the img transform);
                  horizontal offset + overlap via margins gives the same staggered feel as desktop. */}
              <div className="lg:hidden mb-8 w-full max-w-[440px] mx-auto overflow-visible">
                {[
                  { src: 'Marsi-card-2.png', alt: 'Marsi — Marketing Growth Expert', rot: '-4deg', ml: '0%',  delay: '0s' },
                  { src: 'Dora-card-2.png',  alt: 'Dora — Research & Data Analyst',  rot: '3deg',  ml: '16%', delay: '1.5s' },
                  { src: 'Luc-card-2.png',   alt: 'Luc — Personal Assistant',        rot: '-3deg', ml: '5%',  delay: '0.75s' },
                  { src: 'Sally-card-2.png', alt: 'Sally — Sales Manager',           rot: '4deg',  ml: '13%', delay: '2.25s' },
                ].map((c, i) => (
                  <div
                    key={c.src}
                    className="relative w-[80%]"
                    style={{
                      marginTop: i === 0 ? 0 : '-2.5rem',
                      marginLeft: c.ml,
                      transform: `rotate(${c.rot})`,
                      zIndex: i + 1,
                      filter: 'drop-shadow(0 14px 30px rgba(31, 38, 135, 0.18))',
                    }}
                  >
                    <img
                      src={`/images/home/New%20cards/${c.src}`}
                      alt={c.alt}
                      width="440"
                      height="153"
                      loading={i === 0 ? 'eager' : 'lazy'}
                      className="w-full h-auto"
                      style={{ animation: `float-card 6s ease-in-out infinite ${c.delay}` }}
                    />
                  </div>
                ))}
              </div>
              
              <h1
                className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4 md:mb-6 animate-fade-in"
                style={{
                  color: '#14142b',
                  letterSpacing: '-0.02em',
                  lineHeight: '1.15'
                }}>

                Work moves forward —<br />
                <span style={{ color: '#D42A4B' }}>without you chasing it</span>
              </h1>
              <p
                className="text-sm md:text-base lg:text-lg font-normal leading-relaxed mb-4 md:mb-5 animate-fade-in-delay mx-auto lg:mx-0"
                style={{
                  color: 'rgba(20, 20, 43, 0.75)',
                  maxWidth: '560px'
                }}>

                Implify provides you AI teammates that own follow-ups, coordination, and execution across your business — so work doesn't live in your head or get stuck between tools.
              </p>
              <p
                className="text-sm md:text-base font-normal mb-6 md:mb-8 animate-fade-in-delay mx-auto lg:mx-0"
                style={{
                  color: 'rgba(20, 20, 43, 0.6)',
                  maxWidth: '560px'
                }}>

                No setup-heavy workflows. No prompt gymnastics. Just AI teammates that follow through.
              </p>
              <div
                className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-3 md:gap-4 animate-fade-in-delay-2">

                <Button
                  size="lg"
                  onClick={() => window.location.href = 'https://app.implifyai.com'}
                  className="text-sm md:text-base px-6 md:px-8 py-5 md:py-6 transition-all duration-300 hover:scale-105 hover:shadow-lg group relative overflow-hidden glass-accent w-full sm:w-auto"
                  style={{ backgroundColor: 'rgba(255, 92, 92, 0.9)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.2)' }}>

                  <span className="relative z-10 flex items-center justify-center">
                    Build your AI team
                    <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => scrollToSection('how-it-works')}
                  className="text-sm md:text-base px-6 md:px-8 py-5 md:py-6 transition-all duration-300 hover:scale-105 group relative overflow-hidden glass w-full sm:w-auto"
                  style={{ color: '#14142b', background: 'rgba(255, 255, 255, 0.5)', border: '1px solid rgba(20, 20, 43, 0.18)' }}>

                  <span className="relative z-10 flex items-center justify-center group-hover:text-[#0A0A23] transition-colors duration-300">
                    See how it works
                  </span>
                  <div className="absolute inset-0 bg-white scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                </Button>
              </div>
              
              {/* Trust Validators */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 md:gap-6 mt-6 md:mt-8 animate-fade-in-delay-2">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4" style={{ color: '#00BFA6' }} />
                  <span className="text-xs md:text-sm font-medium" style={{ color: 'rgba(20, 20, 43, 0.7)' }}>Free 14-day trial</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4" style={{ color: '#00BFA6' }} />
                  <span className="text-xs md:text-sm font-medium" style={{ color: 'rgba(20, 20, 43, 0.7)' }}>Available 24/7</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4" style={{ color: '#00BFA6' }} />
                  <span className="text-xs md:text-sm font-medium" style={{ color: 'rgba(20, 20, 43, 0.7)' }}>
                    {/* "$0 due today" is true for BOTH flows, so it's the safe default while the
                        flag resolves; only a CONFIRMED legacy flow upgrades to "No credit card
                        required" (which would be false under paywall). Never shows a false claim. */}
                    {signupFlowResolved && activeSignupFlow !== 'paywall_first'
                      ? 'No credit card required'
                      : '$0 due today'}
                  </span>
                </div>
              </div>
            </div>

            {/* Right Column - Agent profile-card cascade (Hidden on mobile/tablet) */}
            <div className="relative h-[500px] lg:h-[600px] hidden lg:flex items-center justify-center overflow-visible">
              <div className="relative w-full h-full">
                {[
                  { src: 'Marsi-card-2.png', alt: 'Marsi — Marketing Growth Expert', top: '0%',  left: '7%',  rot: '-3deg', z: 1, delay: '0s' },
                  { src: 'Dora-card-2.png',  alt: 'Dora — Research & Data Analyst',  top: '24%', left: '0%',  rot: '2deg',  z: 2, delay: '1.5s' },
                  { src: 'Luc-card-2.png',   alt: 'Luc — Personal Assistant',        top: '48%', left: '11%', rot: '-2deg', z: 3, delay: '0.75s' },
                  { src: 'Sally-card-2.png', alt: 'Sally — Sales Manager',           top: '72%', left: '3%',  rot: '3deg',  z: 4, delay: '2.25s' },
                ].map((c, i) => (
                  <div
                    key={c.src}
                    style={{
                      position: 'absolute',
                      top: c.top,
                      left: c.left,
                      width: '460px',
                      transform: `rotate(${c.rot})`,
                      filter: 'drop-shadow(0 20px 40px rgba(31, 38, 135, 0.16))',
                      zIndex: c.z,
                    }}
                  >
                    <img
                      src={`/images/home/New%20cards/${c.src}`}
                      alt={c.alt}
                      width="460"
                      height="160"
                      loading={i === 0 ? 'eager' : 'lazy'}
                      className="w-full h-auto"
                      style={{ animation: `float-card 6s ease-in-out infinite ${c.delay}` }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro video — full-width, no header (right after the hero). */}
      <IntroVideo />

      {/* Outcomes Section */}
      <section
        id="outcomes"
        className="py-16 md:py-24 px-4 md:px-6 lg:px-12 relative"
        data-animate>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight mb-4 md:mb-6" style={{ color: '#14142b', letterSpacing: '-0.02em' }}>
              Daily outcomes
            </h2>
            <p className="text-base md:text-xl" style={{ color: 'rgba(20, 20, 43, 0.6)' }}>
              Every morning, your AI team has already moved the work forward — planning your day, chasing what’s due, prepping your decisions, and drafting your replies. Here’s what that actually looks like.
            </p>
          </div>

          {/* 2x2 grid — descriptive outcomes + product-UI placeholder wells (D19) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {dailyOutcomes.map((o) => (
              <div
                key={o.title}
                className="outcome-card p-5 md:p-8 rounded-2xl flex flex-col"
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.65)',
                  boxShadow: '0 20px 50px rgba(31, 38, 135, 0.10)',
                }}>
                <div className="mb-5">
                  <h3 className="text-lg md:text-2xl font-semibold mb-2" style={{ color: '#14142b' }}>{o.title}</h3>
                  <p className="text-sm md:text-base leading-relaxed" style={{ color: 'rgba(20, 20, 43, 0.7)' }}>{o.body}</p>
                </div>
                {/* Real capture (bare on the aurora, D20 §2) once supplied; otherwise the
                    D19 placeholder well. `layers` renders a floating-cards composition
                    (each shot rounded with a soft drop-shadow, no border); a single
                    `image` renders bare. */}
                {o.layers ? (
                  <div className={`mt-auto relative ${o.aspect || 'aspect-[16/10]'}`}>
                    {o.layers.map((L) => (
                      <div
                        key={L.src}
                        className={`absolute ${L.cls}`}
                        style={{ zIndex: L.z, transform: L.transform, filter: 'drop-shadow(0 12px 28px rgba(31, 38, 135, 0.16))' }}
                      >
                        <img src={`${o.dir}/${L.src}`} alt={L.alt || ''} className="block w-full h-auto rounded-lg" loading="lazy" />
                      </div>
                    ))}
                  </div>
                ) : o.image ? (
                  <img
                    src={o.image}
                    alt={o.imageAlt || o.title}
                    className="mt-auto w-full h-auto"
                    loading="lazy"
                  />
                ) : (
                  <div
                    className="mt-auto aspect-[16/10] rounded-xl overflow-hidden flex items-center justify-center"
                    style={{ border: '1px dashed rgba(20,20,43,0.18)', background: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.35) 0 16px, rgba(255,255,255,0.12) 16px 32px)' }}>
                    <span className="text-xs font-mono text-center px-4" style={{ color: 'rgba(20,20,43,0.45)' }}>placeholder · {o.placeholder}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* How It Works Section — full-bleed product shot; the app where your AI team
          operates. Messaging revolves around the shot; hands off to /how-it-works. */}
      <section id="how-it-works" className="py-16 md:py-32 relative" data-animate>
        {/* Heading — constrained */}
        <div className="max-w-3xl mx-auto text-center px-4 md:px-6 lg:px-12 mb-10 md:mb-14">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight mb-4 md:mb-6" style={{ color: '#14142b', letterSpacing: '-0.02em' }}>
            See the whole operation in one place
          </h2>
          <p className="text-base md:text-xl" style={{ color: 'rgba(20, 20, 43, 0.6)' }}>
            This is Implify — your AI team at work across the tools you already use. Tasks in motion, decisions waiting on your approval, and everything already handled, all in one place.
          </p>
        </div>

        {/* Full-bleed product video — the app where your AI team operates. Muted, looping,
            no controls; autoplays only while on screen (see effect above); holds on the
            poster for reduced-motion. ⚠ The mp4 must be COMPRESSED before shipping (source
            is ~67MB — target ~2–5MB via the ffmpeg encode in docs/notes). */}
        <div className="max-w-[1500px] mx-auto px-4 md:px-6 lg:px-12 mb-12 md:mb-16">
          <div
            className="aspect-[1786/856] rounded-2xl md:rounded-3xl overflow-hidden"
            style={{ boxShadow: '0 20px 50px rgba(31, 38, 135, 0.10)' }}>
            <video
              ref={howItWorksVideoRef}
              className="w-full h-full object-cover"
              muted
              loop
              playsInline
              preload="none"
              poster="/images/home/how-it-works-poster.webp"
              aria-label="A walkthrough of the Implify app — your AI team at work across the tools you already use"
            >
              <source src="/images/home/how-it-works-fullbleed.mp4" type="video/mp4" />
            </video>
          </div>
        </div>

        {/* 3-up: what you're seeing → How It Works pillars */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              { label: 'They work like a team', href: '/how-it-works#orchestration', body: 'Specialist agents pick up tasks, hand off, and run them in parallel — no single generalist doing everything.' },
              { label: 'You stay in control', href: '/how-it-works#control', body: 'Anything customer-facing waits in an approval queue. The agent drafts; your team approves.' },
              { label: 'It remembers your business', href: '/how-it-works#memory', body: 'Your context, tools and past work carry across every agent — nothing re-explained.' },
            ].map((p) => (
              <Link key={p.href} to={p.href} className="group block">
                <h3 className="text-lg md:text-xl font-semibold mb-2 inline-flex items-center gap-2" style={{ color: '#14142b' }}>
                  {p.label}
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" style={{ color: '#D42A4B' }} />
                </h3>
                <p className="text-sm md:text-base leading-relaxed" style={{ color: 'rgba(20, 20, 43, 0.6)' }}>{p.body}</p>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10 md:mt-14">
            <button
              onClick={() => navigate('/how-it-works')}
              className="inline-flex items-center gap-2 text-sm md:text-base font-medium transition-transform duration-200 hover:translate-x-1"
              style={{ color: '#D42A4B' }}>
              See how the whole system works
              <ArrowRight className="h-4 w-4 md:h-5 md:w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Use Cases Section — dept-led auto-accordion + synced preview (built for how you work) */}
      <section id="use-cases" className="py-24 md:py-32 px-4 md:px-6 lg:px-12 relative" data-animate>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight mb-4 md:mb-6" style={{ color: '#14142b', letterSpacing: '-0.02em' }}>
              Built for how you work
            </h2>
            <p className="text-base md:text-xl max-w-3xl mx-auto" style={{ color: 'rgba(20, 20, 43, 0.6)' }}>
              Pick your team and see what changes — the outcomes, the workflows it runs, and the workspace your AI teammates run it in.
            </p>
          </div>
          <DepartmentShowcase departments={homeDepartments} />
        </div>
      </section>

      {/* Integrations Section */}
      <section id="integrations" className="py-32 px-6 lg:px-12 relative" data-animate>
        
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Top Section: 2-column grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20">
            {/* Left Column - Heading and Text */}
            <div>
              <h2
                className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight mb-6"
                style={{ color: '#14142b', letterSpacing: '-0.02em' }}>
                Works with your tools
              </h2>
              <p className="text-xl leading-relaxed mb-6" style={{ color: 'rgba(20, 20, 43, 0.6)' }}>
                With Implify, you can easily connect the tools you already use, and our Agents will slot right into your everyday workflow.
              </p>
              <button
                onClick={() => navigate('/how-it-works#integrations')}
                className="inline-flex items-center gap-2 text-sm md:text-base font-medium transition-transform duration-200 hover:translate-x-1"
                style={{ color: '#D42A4B' }}>
                See how integrations work
                <ArrowRight className="h-4 w-4 md:h-5 md:w-5" />
              </button>
            </div>
            
            {/* Right Column - Circular Icon Display */}
            <div className="relative flex items-center justify-center" style={{ height: '400px' }}>
              {/* Center Favicon in glass container */}
              <div 
                className="absolute z-10 w-28 h-28 rounded-full flex items-center justify-center"
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.65)',
                  boxShadow: '0 20px 50px rgba(31, 38, 135, 0.10)'
                }}>
                <img
                  src="/images/Favicon-dark.png"
                  alt="Implify" 
                  className="w-16 h-16 object-contain"
                  width="64"
                  height="64"
                />
              </div>
              
              {/* Orbital Ring - Revolving Container */}
              <div 
                className="revolving-orbit absolute"
                style={{ width: '300px', height: '300px' }}>
                {[
                  { name: 'Gmail', angle: 0 },
                  { name: 'HubSpot', angle: 45 },
                  { name: 'Instagram', angle: 90 },
                  { name: 'Teams', angle: 135 },
                  { name: 'Drive', angle: 180 },
                  { name: 'Notion', angle: 225 },
                  { name: 'Zoom', angle: 270 },
                  { name: 'Outlook', angle: 315 }
                ].map((icon, idx) => {
                  const radius = 130;
                  const angleRad = (icon.angle * Math.PI) / 180;
                  const x = Math.cos(angleRad) * radius + 150;
                  const y = Math.sin(angleRad) * radius + 150;
                  const logoUrl = integrationLogos[icon.name]; // Use colored logos
                  
                  return (
                    <div
                      key={idx}
                      className="absolute w-14 h-14 rounded-2xl flex items-center justify-center overflow-hidden"
                      style={{
                        left: `${x - 28}px`,
                        top: `${y - 28}px`,
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                      }}>
                      <div className="revolving-icon-inner flex items-center justify-center w-full h-full">
                        <img 
                          src={logoUrl} 
                          alt={icon.name} 
                          className="w-8 h-8 object-contain"
                          loading="lazy"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div 
                          className="w-8 h-8 rounded-lg items-center justify-center text-white text-sm font-bold hidden"
                          style={{ backgroundColor: '#FF5C5C' }}>
                          {icon.name.charAt(0)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          
          {/* Bottom Section: Flowing Card Rows with Mask Fade Effect */}
          <div 
            className="relative overflow-hidden"
            style={{
              maskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
              WebkitMaskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
            }}
          >
            {/* Row 1 - Left to Right */}
            <div className="flex gap-4 mb-6" style={{ animation: 'scrollLTR 30s linear infinite' }}>
              {[...Array(14)].map((_, idx) => {
                const tools = ['Gmail', 'Facebook', 'Notion', 'HubSpot', 'LinkedIn', 'Zoom', 'Teams'];
                const tool = tools[idx % tools.length];
                const logoUrl = integrationLogos[tool];
                return (
                  <div
                    key={idx}
                    className="flex-shrink-0 flex items-center gap-3 px-5 py-3 rounded-xl"
                    style={{
                      minWidth: '160px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                      border: '1px solid rgba(255, 255, 255, 0.65)',
                      boxShadow: '0 4px 16px rgba(31, 38, 135, 0.10)'
                    }}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden bg-white">
                      <img 
                        src={logoUrl} 
                        alt={tool} 
                        className="w-6 h-6 object-contain"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `<span class="text-sm font-bold text-gray-600">${tool.charAt(0)}</span>`;
                        }}
                      />
                    </div>
                    <span className="text-sm font-medium" style={{ color: '#14142b' }}>{tool}</span>
                  </div>
                );
              })}
            </div>
            
            {/* Row 2 - Right to Left (AI models) */}
            <div className="flex gap-4 mb-6" style={{ animation: 'scrollRTL 35s linear infinite' }}>
              {[...Array(14)].map((_, idx) => {
                const tools = ['Figma', 'GitHub', 'Kling', 'Gemini', 'Claude', 'ElevenLabs', 'Perplexity'];
                const tool = tools[idx % tools.length];
                const logoUrl = integrationLogos[tool];
                return (
                  <div
                    key={idx}
                    className="flex-shrink-0 flex items-center gap-3 px-5 py-3 rounded-xl"
                    style={{
                      minWidth: '160px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                      border: '1px solid rgba(255, 255, 255, 0.65)',
                      boxShadow: '0 4px 16px rgba(31, 38, 135, 0.10)'
                    }}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden bg-white">
                      <img 
                        src={logoUrl} 
                        alt={tool} 
                        className="w-6 h-6 object-contain"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `<span class="text-sm font-bold text-gray-600">${tool.charAt(0)}</span>`;
                        }}
                      />
                    </div>
                    <span className="text-sm font-medium" style={{ color: '#14142b' }}>{tool}</span>
                  </div>
                );
              })}
            </div>
            
            {/* Row 3 - Left to Right */}
            <div className="flex gap-4" style={{ animation: 'scrollLTR 32s linear infinite' }}>
              {[...Array(14)].map((_, idx) => {
                const tools = ['Drive', 'Calendar', 'Asana', 'Stripe', 'QuickBooks', 'Dropbox', 'Jira'];
                const tool = tools[idx % tools.length];
                const logoUrl = integrationLogos[tool];
                return (
                  <div
                    key={idx}
                    className="flex-shrink-0 flex items-center gap-3 px-5 py-3 rounded-xl"
                    style={{
                      minWidth: '160px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                      border: '1px solid rgba(255, 255, 255, 0.65)',
                      boxShadow: '0 4px 16px rgba(31, 38, 135, 0.10)'
                    }}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden bg-white">
                      <img 
                        src={logoUrl} 
                        alt={tool} 
                        className="w-6 h-6 object-contain"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `<span class="text-sm font-bold text-gray-600">${tool.charAt(0)}</span>`;
                        }}
                      />
                    </div>
                    <span className="text-sm font-medium" style={{ color: '#14142b' }}>{tool}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Done For You Section — hub → /services (new for CP6). Benefit beat for buyers
          who'd rather not self-configure; D8a-honest (we set up, your team runs). */}
      <section id="services" className="py-24 md:py-32 px-4 md:px-6 lg:px-12 relative" data-animate>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-center">
            {/* Text — 40% */}
            <div className="lg:col-span-2">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight mb-4 md:mb-6" style={{ color: '#14142b', letterSpacing: '-0.02em' }}>
                Prefer we set it up for you?
              </h2>
              <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: 'rgba(20, 20, 43, 0.75)' }}>
                Don’t want to configure it yourself? We design the strategy and build your AI setup on the tools you already use — your CRM, your inbox, your stack. Your team runs it from week one.
              </p>
              <p className="text-sm md:text-base mb-8" style={{ color: 'rgba(20, 20, 43, 0.6)' }}>
                Set up by us, run by your team — and nothing reaches a customer without someone on your side approving it.
              </p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-3 md:gap-4">
                <Button
                  size="lg"
                  onClick={() => window.open('https://calendly.com/implifyai-marketing/30min', '_blank', 'noopener,noreferrer')}
                  className="text-sm md:text-base px-6 md:px-8 py-5 md:py-6 transition-all duration-300 hover:scale-105 hover:shadow-lg group relative overflow-hidden glass-accent w-full sm:w-auto"
                  style={{ backgroundColor: 'rgba(255, 92, 92, 0.9)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                  <span className="relative z-10 flex items-center justify-center">
                    Book a call
                    <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/services')}
                  className="text-sm md:text-base px-6 md:px-8 py-5 md:py-6 transition-all duration-300 hover:scale-105 group relative overflow-hidden glass w-full sm:w-auto"
                  style={{ color: '#14142b', background: 'rgba(255, 255, 255, 0.5)', border: '1px solid rgba(20, 20, 43, 0.18)' }}>
                  <span className="relative z-10 flex items-center justify-center group-hover:text-[#0A0A23] transition-colors duration-300">
                    See how we work
                  </span>
                  <div className="absolute inset-0 bg-white scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                </Button>
              </div>
            </div>
            {/* Visual — 60%: the intelligence-layer diagram. No product UI exists for
                "done for you", so instead of a screenshot we draw what we actually set up —
                your systems → shared knowledge → shared context → each team's role-scoped
                agents. Reused from ServicesPage (coded, no image request). */}
            <div className="lg:col-span-3">
              <IntelligenceLayerDiagram />
            </div>
          </div>
        </div>
      </section>

      {/* Founder Vision Section - Glassmorphism Design (moved above pricing: belief → price → act) */}
      <section id="vision" className="py-32 px-6 lg:px-12 relative" data-animate>
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Mobile/Tablet: Logo above heading */}
          <div className="lg:hidden flex justify-center mb-12">
            <div className="relative">
              <div className="absolute inset-0 rounded-full" style={{ animation: 'pulse-ring 3s ease-out infinite', border: '2px solid rgba(255, 92, 92, 0.4)' }} />
              <div className="absolute inset-[-15px] rounded-full" style={{ animation: 'pulse-ring 3s ease-out infinite 0.5s', border: '2px solid rgba(255, 92, 92, 0.3)' }} />
              <div className="absolute inset-[-30px] rounded-full" style={{ animation: 'pulse-ring 3s ease-out infinite 1s', border: '2px solid rgba(255, 92, 92, 0.2)' }} />
              <div className="relative w-28 h-28 rounded-full flex items-center justify-center" style={{ background: '#FFFFFF', boxShadow: 'inset 4px 4px 12px rgba(0, 0, 0, 0.1), inset -2px -2px 8px rgba(255, 255, 255, 0.9), 0 8px 32px rgba(0, 0, 0, 0.2)', animation: 'float 4s ease-in-out infinite' }}>
                <img src="/images/Favicon-dark.png" alt="Implify" className="w-16 h-16 object-contain" width="64" height="64" loading="lazy" />
              </div>
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight mb-16 text-center" style={{ color: '#14142b', letterSpacing: '-0.02em' }}>
            Why we built Implify
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-5xl mx-auto">
            <div className="relative p-8 lg:p-10 rounded-3xl" style={{ background: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.65)', boxShadow: '0 20px 50px rgba(31, 38, 135, 0.10)' }}>
              <div className="absolute -left-2 -top-4 text-8xl font-serif leading-none" style={{ color: '#FF5C5C', opacity: 0.6 }}>"</div>
              <blockquote className="relative z-10">
                <p className="text-xl lg:text-2xl leading-relaxed" style={{ color: 'rgba(20, 20, 43, 0.75)' }}>
                  We built Implify because we were tired of smart AI that didn't help us ship work. We needed a system that understood how real teams operate — with handoffs, parallel execution, and clear ownership. Not a chatbot. Not a single agent trying to do everything. A workforce that works like you do.
                </p>
                <footer className="mt-6">
                  <p className="text-base font-medium" style={{ color: '#FF5C5C' }}>— The Implify Team</p>
                </footer>
              </blockquote>
            </div>
            <div className="hidden lg:flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 rounded-full" style={{ animation: 'pulse-ring 3s ease-out infinite', border: '2px solid rgba(255, 92, 92, 0.4)' }} />
                <div className="absolute inset-[-20px] rounded-full" style={{ animation: 'pulse-ring 3s ease-out infinite 0.5s', border: '2px solid rgba(255, 92, 92, 0.3)' }} />
                <div className="absolute inset-[-40px] rounded-full" style={{ animation: 'pulse-ring 3s ease-out infinite 1s', border: '2px solid rgba(255, 92, 92, 0.2)' }} />
                <div className="relative w-40 h-40 rounded-full flex items-center justify-center" style={{ background: '#FFFFFF', boxShadow: 'inset 4px 4px 12px rgba(0, 0, 0, 0.1), inset -2px -2px 8px rgba(255, 255, 255, 0.9), 0 8px 32px rgba(0, 0, 0, 0.2)', animation: 'float 4s ease-in-out infinite' }}>
                  <img src="/images/Favicon-dark.png" alt="Implify" className="w-24 h-24 object-contain" width="96" height="96" loading="lazy" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section
        id="pricing"
        className="py-32 px-6 lg:px-12 relative"
        data-animate>
        

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight mb-6"
              style={{ color: '#14142b', letterSpacing: '-0.02em' }}>
              Simple, transparent pricing
            </h2>
            <p className="text-xl" style={{ color: 'rgba(20, 20, 43, 0.6)' }}>
              Every Implify agent included, one simple price.
            </p>
          </div>

          {/* 2-Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 max-w-5xl mx-auto">
            {/* Left Column - Pricing Card with glassmorphism */}
            <div
              className="p-10 rounded-3xl text-center lg:text-left"
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.65)',
                boxShadow: '0 20px 50px rgba(31, 38, 135, 0.10)'
              }}>

              <div className="text-6xl font-light mb-4" style={{ color: '#14142b' }}>
                $12.99
                <span className="text-2xl" style={{ color: 'rgba(20, 20, 43, 0.6)' }}>
                  /month
                </span>
              </div>
              <p className="text-lg mb-8" style={{ color: 'rgba(20, 20, 43, 0.6)' }}>
                Full access to every Implify agent. Billed annually.
              </p>
              <Button
                size="lg"
                onClick={() => window.location.href = 'https://app.implifyai.com'}
                className="text-base px-8 py-6 transition-all duration-300 hover:scale-105 w-full lg:w-auto"
                style={{ backgroundColor: '#FF5C5C', color: 'white' }}>
                Start 14-day free trial
              </Button>
              <p className="text-sm mt-4" style={{ color: 'rgba(20, 20, 43, 0.6)' }}>
                {signupFlowResolved
                  ? activeSignupFlow === 'paywall_first'
                    ? '$0 due today • '
                    : 'No credit card required • '
                  : ''}Cancel anytime
              </p>
            </div>

            {/* Right Column - Trust Cards with glassmorphism on each card */}
            <div className="space-y-4">
              {/* Trust Card 1 */}
              <div
                className="p-5 rounded-xl"
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.65)',
                  boxShadow: '0 20px 50px rgba(31, 38, 135, 0.10)'
                }}>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-6 w-6 flex-shrink-0 mt-0.5" style={{ color: '#00BFA6' }} />
                  <div>
                    <h4 className="text-base font-medium mb-1" style={{ color: '#14142b' }}>
                      Trusted by teams that ship
                    </h4>
                    <p className="text-sm" style={{ color: 'rgba(20, 20, 43, 0.6)' }}>
                      From startups to enterprises, teams rely on Implify to automate their workflows daily.
                    </p>
                  </div>
                </div>
              </div>

              {/* Trust Card 2 */}
              <div
                className="p-5 rounded-xl"
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.65)',
                  boxShadow: '0 20px 50px rgba(31, 38, 135, 0.10)'
                }}>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-6 w-6 flex-shrink-0 mt-0.5" style={{ color: '#00BFA6' }} />
                  <div>
                    <h4 className="text-base font-medium mb-1" style={{ color: '#14142b' }}>
                      99.9% uptime guarantee
                    </h4>
                    <p className="text-sm" style={{ color: 'rgba(20, 20, 43, 0.6)' }}>
                      Enterprise-grade reliability with continuous monitoring around the clock.
                    </p>
                  </div>
                </div>
              </div>

              {/* Trust Card 3 */}
              <div
                className="p-5 rounded-xl"
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.65)',
                  boxShadow: '0 20px 50px rgba(31, 38, 135, 0.10)'
                }}>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-6 w-6 flex-shrink-0 mt-0.5" style={{ color: '#00BFA6' }} />
                  <div>
                    <h4 className="text-base font-medium mb-1" style={{ color: '#14142b' }}>
                      Dedicated support
                    </h4>
                    <p className="text-sm" style={{ color: 'rgba(20, 20, 43, 0.6)' }}>
                      Get help when you need it. Our team responds within 2 hours on average.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section - Hero Style Glassmorphism */}
      <section
        id="cta"
        className="py-24 px-6 lg:px-12 relative overflow-hidden"
        data-animate>

        <div className="max-w-5xl mx-auto relative z-10">
          {/* Master Card Container - Glassmorphism like Hero */}
          <div
            className="rounded-3xl p-10 lg:p-16 text-center"
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.65)',
              boxShadow: '0 20px 50px rgba(31, 38, 135, 0.10)'
            }}>
            
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight mb-6"
              style={{ color: '#14142b', letterSpacing: '-0.02em' }}>
              Build your AI team in minutes
            </h2>
            <p className="text-lg lg:text-xl mb-8 max-w-2xl mx-auto" style={{ color: 'rgba(20, 20, 43, 0.75)' }}>
              Start with one agent or assemble your full workforce. Your choice.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => window.location.href = 'https://app.implifyai.com'}
                className="text-base px-8 py-6 transition-all duration-300 hover:scale-105 hover:shadow-lg group relative overflow-hidden glass-accent"
                style={{ backgroundColor: 'rgba(255, 92, 92, 0.9)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                <span className="relative z-10 flex items-center justify-center">
                  Get started for free
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => window.open('https://calendly.com/implifyai-marketing/30min', '_blank', 'noopener,noreferrer')}
                className="text-base px-8 py-6 transition-all duration-300 hover:scale-105 group relative overflow-hidden glass"
                style={{ borderColor: 'rgba(20, 20, 43, 0.18)', color: '#14142b', background: 'rgba(255, 255, 255, 0.5)' }}>
                <span className="relative z-10 flex items-center justify-center group-hover:text-[#0A0A23] transition-colors duration-300">
                  Book a demo
                </span>
                <div className="absolute inset-0 bg-white scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </Button>
            </div>
            <p className="text-sm mt-6" style={{ color: 'rgba(20, 20, 43, 0.6)' }}>
              {signupFlowResolved
                ? activeSignupFlow === 'paywall_first'
                  ? '$0 due today • '
                  : 'No credit card required • '
                : ''}Cancel anytime
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer variant="light" />
    </div>);

};

export default HomePage;